/**
 * Autopilot — types + API. Sprint 7 · Project Symphony.
 */
import { http } from "@/shared/api/http";

export type TrustLevel = "advisor" | "assistant" | "operator" | "executive";
export type AutopilotState = "enabled" | "paused" | "stopped";

export interface AutopilotAction {
  workspace_id: string;
  campaign_id: string | null;
  action: string;
  action_label: string;
  reason: string;
  rule_authorized: string;
  is_pending_approval: boolean;
  confidence: {
    score: number;
    label: string;
    indicator: string;
    band: string;
    reasons: string[];
  } | null;
  budget: {
    month_to_date_usd: number;
    monthly_limit_usd: number;
    within_limits: boolean;
    reason: string;
  } | null;
  metadata: Record<string, unknown>;
  occurred_at: string;
}

export interface DepartmentAutonomyRow {
  key: string;
  label: string;
  activity: string;
  status: "active" | "standby";
}

export interface AutopilotDashboard {
  workspace_id: string;
  state: AutopilotState;
  trust_level: TrustLevel;
  configured_trust_level: TrustLevel;
  autopilot_max_level: TrustLevel;
  readiness_max_level: TrustLevel;
  readiness_checks: AutopilotReadinessCheck[];
  ceiling_enforced: boolean;
  readiness_enforced: boolean;
  trust_label: string;
  trust_tagline: string;
  enabled_at: string | null;
  paused_reason: string | null;
  last_tick_at: string | null;
  tick_count: number;
  last_action: string;
  last_action_at: string | null;
  last_action_reason: string | null;
  next_scheduled_task: string;
  next_scheduled_at: string | null;
  health_indicator: string;
  health_label: string;
  departments_active: DepartmentAutonomyRow[];
  campaigns_running: number;
  pending_reviews: number;
  blocked_tasks: number;
  recent_actions: AutopilotAction[];
}

export interface AutopilotReadinessCheck {
  key: string;
  label: string;
  required_for: TrustLevel;
  passed: boolean;
  action_path: string;
}

export interface MorningBrief {
  workspace_id: string;
  generated_at: string;
  kind: "morning";
  headline: string;
  lines: string[];
  counts: {
    published_yesterday: number;
    assets_prepared_yesterday: number;
    scheduled_today: number;
    pending_reviews: number;
  };
}

export interface WeeklyBrief {
  workspace_id: string;
  generated_at: string;
  kind: "weekly";
  headline: string;
  sections: { label: string; value: string }[];
  recommendations: string[];
}

export interface PublishingSnapshot {
  workspace_id: string;
  campaign_id: string;
  status: "idle" | "waiting" | "publishing" | "completed";
  slots_total: number;
  slots_published: number;
  started_at: string | null;
  completed_at: string | null;
  last_pushed_at?: string | null;
  reason: string | null;
  progress_pct?: number;
}

export const getAutopilotDashboard = (): Promise<AutopilotDashboard> =>
  http<AutopilotDashboard>("/orchestration/autopilot/dashboard");

export const enableAutopilot = (): Promise<unknown> =>
  http("/orchestration/autopilot/enable", { method: "POST" });

export const pauseAutopilot = (reason?: string): Promise<unknown> =>
  http("/orchestration/autopilot/pause", {
    method: "POST",
    body: reason ? { reason } : {},
  });

export const resumeAutopilot = (): Promise<unknown> =>
  http("/orchestration/autopilot/resume", { method: "POST" });

export const emergencyStopAutopilot = (): Promise<unknown> =>
  http("/orchestration/autopilot/emergency-stop", { method: "POST" });

export const setTrustLevel = (level: TrustLevel): Promise<unknown> =>
  http("/orchestration/autopilot/trust-level", {
    method: "POST",
    body: { trust_level: level },
  });

export const runAutopilotTick = (): Promise<unknown> =>
  http("/orchestration/autopilot/tick", { method: "POST" });

export const getMorningBrief = (): Promise<MorningBrief> =>
  http<MorningBrief>("/orchestration/autopilot/brief/daily");

export const getWeeklyBrief = (): Promise<WeeklyBrief> =>
  http<WeeklyBrief>("/orchestration/autopilot/brief/weekly");

export const getPublishingSnapshot = (campaignId: string): Promise<PublishingSnapshot> =>
  http<PublishingSnapshot>(`/orchestration/autopilot/publishing/${campaignId}`);
